/**
 * Mapping Properties Panel Component
 * Edit properties of selected mapping region
 */

import React from 'react';
import {
  MappingRegion,
  FONT_FAMILIES,
  FONT_SIZES,
  DATE_FORMATS,
  CURRENCY_FORMATS,
  AVAILABLE_FIELDS,
} from './types';

interface MappingPropertiesProps {
  mapping: MappingRegion | null;
  onChange: (updates: Partial<MappingRegion>) => void;
  onDelete: () => void;
  pageCount?: number;
}

export const MappingProperties: React.FC<MappingPropertiesProps> = ({
  mapping,
  onChange,
  onDelete,
  pageCount = 1,
}) => {
  if (!mapping) {
    return (
      <div className="text-center text-gray-500 py-8">
        <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
        </svg>
        <p className="text-sm">Select a field to edit its properties</p>
        <p className="text-xs mt-2">Or click a field in the palette and draw on the PDF</p>
      </div>
    );
  }

  const fieldDef = AVAILABLE_FIELDS.find(f => f.name === mapping.fieldName);

  return (
    <div className="space-y-4">
      {/* Field info */}
      <div className="pb-3 border-b">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xl">{fieldDef?.icon}</span>
          <h3 className="font-semibold text-gray-900">{fieldDef?.label || mapping.fieldName}</h3>
        </div>
        <p className="text-xs text-gray-500">{fieldDef?.description}</p>
        <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 capitalize">
          {mapping.fieldType}
        </span>
      </div>

      {/* Position */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Position (points)</h4>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-gray-500 block mb-1">X</label>
            <input
              type="number"
              value={Math.round(mapping.x)}
              onChange={e => onChange({ x: parseFloat(e.target.value) || 0 })}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Y</label>
            <input
              type="number"
              value={Math.round(mapping.y)}
              onChange={e => onChange({ y: parseFloat(e.target.value) || 0 })}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Width</label>
            <input
              type="number"
              value={Math.round(mapping.width)}
              onChange={e => onChange({ width: parseFloat(e.target.value) || 20 })}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Height</label>
            <input
              type="number"
              value={Math.round(mapping.height)}
              onChange={e => onChange({ height: parseFloat(e.target.value) || 10 })}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Page Number */}
      {pageCount > 1 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Page</h4>
          <select
            value={mapping.pageNumber}
            onChange={e => onChange({ pageNumber: parseInt(e.target.value) })}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {Array.from({ length: pageCount }, (_, i) => i + 1).map(page => (
              <option key={page} value={page}>Page {page}</option>
            ))}
          </select>
        </div>
      )}

      {/* Font Settings */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Font</h4>
        <div className="space-y-2">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Family</label>
            <select
              value={mapping.fontFamily}
              onChange={e => onChange({ fontFamily: e.target.value })}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {FONT_FAMILIES.map(font => (
                <option key={font} value={font}>{font}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Size (pt)</label>
              <select
                value={mapping.fontSize}
                onChange={e => onChange({ fontSize: parseFloat(e.target.value) })}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {FONT_SIZES.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Color</label>
              <div className="flex gap-1">
                <input
                  type="color"
                  value={mapping.fontColor}
                  onChange={e => onChange({ fontColor: e.target.value })}
                  className="w-10 h-8 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={mapping.fontColor}
                  onChange={e => onChange({ fontColor: e.target.value })}
                  className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Text Alignment */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Alignment</h4>
        <div className="flex gap-1">
          {(['left', 'center', 'right'] as const).map(align => (
            <button
              key={align}
              onClick={() => onChange({ textAlign: align })}
              className={`
                flex-1 py-2 border rounded text-sm capitalize transition-colors
                ${mapping.textAlign === align
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }
              `}
            >
              {align === 'left' && (
                <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h8a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h8a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              )}
              {align === 'center' && (
                <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm2 4a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm-2 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm2 4a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              )}
              {align === 'right' && (
                <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm4 4a1 1 0 011-1h8a1 1 0 110 2H8a1 1 0 01-1-1zm-4 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm4 4a1 1 0 011-1h8a1 1 0 110 2H8a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Format Pattern (for dates and currency) */}
      {mapping.fieldType === 'date' && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Date Format</h4>
          <select
            value={mapping.formatPattern || 'MM/DD/YYYY'}
            onChange={e => onChange({ formatPattern: e.target.value })}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {DATE_FORMATS.map(fmt => (
              <option key={fmt.value} value={fmt.value}>{fmt.label}</option>
            ))}
          </select>
        </div>
      )}

      {mapping.fieldType === 'currency' && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Currency Format</h4>
          <select
            value={mapping.formatPattern || '$#,##0.00'}
            onChange={e => onChange({ formatPattern: e.target.value })}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {CURRENCY_FORMATS.map(fmt => (
              <option key={fmt.value} value={fmt.value}>{fmt.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* Line Height */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Line Height</h4>
        <input
          type="number"
          step="0.1"
          min="0.5"
          max="3"
          value={mapping.lineHeight}
          onChange={e => onChange({ lineHeight: parseFloat(e.target.value) || 1.2 })}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Max Lines (for text fields) */}
      {mapping.fieldType === 'text' && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Max Lines</h4>
          <input
            type="number"
            min="1"
            max="20"
            value={mapping.maxLines || ''}
            placeholder="No limit"
            onChange={e => onChange({ maxLines: e.target.value ? parseInt(e.target.value) : undefined })}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      )}

      {/* Delete Button */}
      <div className="pt-4 border-t">
        <button
          onClick={onDelete}
          className="w-full py-2 bg-red-50 text-red-600 rounded border border-red-200 hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          Delete Field
        </button>
        <p className="text-xs text-gray-500 mt-1 text-center">
          Or press Delete/Backspace key
        </p>
      </div>
    </div>
  );
};

export default MappingProperties;
